/**
 * Phase 8 T-26 §29.D chip 4 — Compare drawer.
 *
 * Side-by-side 2-col (or 3-col if 3 are ticked) comparison surface,
 * presented in a capsule-clipped right-slide `<Drawer>` (§10). Called
 * from the `[Compare]` chip in `<NextStepBridge>` when at least 2
 * candidates are ticked.
 *
 * Rows shown per candidate (top → bottom):
 *   - Avatar + name + current title / company.
 *   - Composite score (0–100) with a per-candidate delta vs the
 *     leader (Δ badge).
 *   - Top skills (matched keywords) with delta markers for which
 *     skills are shared vs unique.
 *   - Experience years (total) with delta vs the leader.
 *   - Location.
 *
 * Deltas are computed *relative to the first (highest-score) column*
 * — the leader's deltas read as `—` and the others show `+2` / `-3` /
 * matching-skills-count etc.
 *
 * Over 3 ticked candidates: the drawer clips at 3, with a small "+N
 * more" affordance above the grid noting the excess.
 */

import { useMemo } from 'react';
import { Minus, Plus, SplitSquareHorizontal } from 'lucide-react';
import { Drawer } from '@shared/components/ui/Drawer';
import type { MatchResult } from '../types';

export const COMPARE_MAX_COLS = 3;

interface CompareDrawerProps {
  open: boolean;
  onClose: () => void;
  /** All ticked candidate matches. Clipped at 3 for the grid. */
  candidates: MatchResult[];
}

export function CompareDrawer({ open, onClose, candidates }: CompareDrawerProps) {
  const visible = useMemo(
    () => candidates.slice(0, COMPARE_MAX_COLS),
    [candidates],
  );
  const overflow = Math.max(0, candidates.length - COMPARE_MAX_COLS);
  const leader = visible[0] ?? null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      side="right"
      title="Compare candidates"
      ariaLabel="Compare candidates"
      panelMaxWidthPx={visible.length >= 3 ? 820 : 620}
      panelClassName="rounded-[10px]"
    >
      <div className="flex h-full min-h-0 flex-col">
        <div
          className="border-b px-4 py-3"
          style={{ borderColor: 'var(--vscode-border-primary)' }}
        >
          <div className="flex items-center gap-2 text-[13px]">
            <SplitSquareHorizontal
              className="h-4 w-4"
              aria-hidden
              style={{ color: 'var(--ggj-accent,#10B981)' }}
            />
            <span
              className="font-medium"
              style={{ color: 'var(--vscode-text-primary)' }}
            >
              {visible.length} candidates side-by-side
            </span>
            {overflow > 0 ? (
              <span
                className="ml-auto text-[11px]"
                style={{ color: 'var(--vscode-text-tertiary)' }}
              >
                +{overflow} more ticked — showing top {COMPARE_MAX_COLS}
              </span>
            ) : null}
          </div>
        </div>

        {visible.length === 0 ? (
          <div
            className="flex flex-1 items-center justify-center p-6 text-[13px]"
            style={{ color: 'var(--vscode-text-tertiary)' }}
          >
            No candidates ticked yet.
          </div>
        ) : (
          <div
            className="grid min-h-0 flex-1 overflow-auto"
            style={{
              gridTemplateColumns: `repeat(${visible.length}, minmax(0,1fr))`,
            }}
          >
            {visible.map((m, idx) => (
              <CompareColumn
                key={`${m.candidate_id}-${m.job_id}`}
                match={m}
                leader={leader}
                isLeader={idx === 0}
              />
            ))}
          </div>
        )}
      </div>
    </Drawer>
  );
}

function CompareColumn({
  match,
  leader,
  isLeader,
}: {
  match: MatchResult;
  leader: MatchResult | null;
  isLeader: boolean;
}) {
  const scoreDelta =
    leader && !isLeader
      ? match.composite_score - leader.composite_score
      : 0;

  const expYears = match.candidate?.years_exp ?? match.candidate?.total_experience_years ?? null;
  const leaderYears =
    leader?.candidate?.years_exp ?? leader?.candidate?.total_experience_years ?? null;
  const expDelta =
    !isLeader && typeof expYears === 'number' && typeof leaderYears === 'number'
      ? expYears - leaderYears
      : null;

  const topSkills = (match.matched_skills ?? []).slice(0, 8);
  const leaderSkills = new Set(leader?.matched_skills ?? []);
  const sharedCount = topSkills.filter((s) => leaderSkills.has(s)).length;
  const uniqueCount = topSkills.length - sharedCount;

  const location = match.candidate?.location ?? '—';
  const name = match.candidate?.full_name ?? 'Candidate';
  const title = match.candidate?.current_title ?? '';
  const company = match.candidate?.current_company ?? '';

  return (
    <div
      className="flex flex-col gap-3 border-r px-4 py-4 last:border-r-0"
      style={{ borderColor: 'var(--vscode-border-primary)' }}
    >
      {/* Name + title */}
      <div>
        <p
          className="truncate text-[14px] font-semibold"
          style={{ color: 'var(--vscode-text-primary)' }}
          title={name}
        >
          {name}
        </p>
        {title || company ? (
          <p
            className="truncate text-[12px]"
            style={{ color: 'var(--vscode-text-tertiary)' }}
          >
            {[title, company].filter(Boolean).join(' · ')}
          </p>
        ) : null}
      </div>

      {/* Score + delta */}
      <CompareRow label="Composite score">
        <div className="flex items-baseline gap-2">
          <span
            className="text-[22px] font-semibold tabular-nums"
            style={{ color: 'var(--ggj-accent,#10B981)' }}
          >
            {Math.round(match.composite_score)}
          </span>
          <DeltaBadge value={isLeader ? null : scoreDelta} suffix="" />
        </div>
      </CompareRow>

      {/* Top skills */}
      <CompareRow
        label={`Top skills${isLeader ? '' : ` · ${sharedCount} shared · ${uniqueCount} unique`}`}
      >
        <ul className="flex flex-wrap gap-1">
          {topSkills.length === 0 ? (
            <li
              className="text-[12px]"
              style={{ color: 'var(--vscode-text-tertiary)' }}
            >
              —
            </li>
          ) : (
            topSkills.map((s) => {
              const isShared = !isLeader && leaderSkills.has(s);
              return (
                <li
                  key={s}
                  className="inline-flex items-center rounded-full border px-2 py-0.5 text-[11px]"
                  style={{
                    borderColor: isShared
                      ? 'var(--ggj-accent,#10B981)'
                      : 'var(--vscode-border-primary)',
                    color: isShared
                      ? '#065F46'
                      : 'var(--vscode-text-secondary)',
                    background: isShared
                      ? 'var(--ggj-accent-soft,#D1FAE5)'
                      : 'transparent',
                  }}
                >
                  {s}
                </li>
              );
            })
          )}
        </ul>
      </CompareRow>

      {/* Experience */}
      <CompareRow label="Experience">
        <div className="flex items-baseline gap-2 text-[13px]">
          <span
            className="tabular-nums"
            style={{ color: 'var(--vscode-text-primary)' }}
          >
            {typeof expYears === 'number' ? `${expYears}y` : '—'}
          </span>
          <DeltaBadge value={expDelta} suffix="y" />
        </div>
      </CompareRow>

      {/* Location */}
      <CompareRow label="Location">
        <span
          className="text-[13px]"
          style={{ color: 'var(--vscode-text-primary)' }}
        >
          {location || '—'}
        </span>
      </CompareRow>
    </div>
  );
}

function CompareRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p
        className="mb-1 text-[10px] font-semibold uppercase tracking-wide"
        style={{ color: 'var(--vscode-text-tertiary)' }}
      >
        {label}
      </p>
      {children}
    </div>
  );
}

function DeltaBadge({
  value,
  suffix,
}: {
  value: number | null;
  suffix?: string;
}) {
  if (value == null || value === 0) {
    return (
      <span
        className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px]"
        style={{
          background: 'var(--vscode-bg-hover)',
          color: 'var(--vscode-text-tertiary)',
        }}
      >
        —
      </span>
    );
  }
  const positive = value > 0;
  return (
    <span
      className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums"
      style={{
        background: positive
          ? 'rgb(209 250 229)'
          : 'rgba(239,68,68,0.12)',
        color: positive ? 'rgb(6 95 70)' : 'rgb(185,28,28)',
      }}
    >
      {positive ? (
        <Plus className="h-2.5 w-2.5" aria-hidden />
      ) : (
        <Minus className="h-2.5 w-2.5" aria-hidden />
      )}
      {Math.abs(value).toFixed(value % 1 === 0 ? 0 : 1)}
      {suffix}
    </span>
  );
}
